import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Trophy, CalendarClock, Coins, HelpCircle, FileText, Network } from 'lucide-react';
import type { PyramidaUiState } from '../hooks/usePyramidaState';
import {
  CONTRACT_ADDRESS,
  CONTRACT_PARAMS,
  IS_CONTRACT_CONFIGURED,
  TON_NETWORK,
} from '../ton/config';
import { ExplorerLink } from './ExplorerLink';
import { VerifiedBadge } from './VerifiedBadge';
import { formatInt, formatTon } from '../lib/format';

interface AboutProps {
  state: PyramidaUiState;
}

export function About({ state }: AboutProps) {
  const { t } = useTranslation();
  const firstLoad = IS_CONTRACT_CONFIGURED && state.lastUpdated === null;
  const stat = (node: ReactNode) =>
    firstLoad ? <span className="skeleton skeleton--value" /> : node;

  const faq: { q: string; a: string }[] = [
    { q: t('about.faq.buyQ'), a: t('about.faq.buyA', { min: formatTon(CONTRACT_PARAMS.minEntryTon) }) },
    {
      q: t('about.faq.winnerQ'),
      a: t('about.faq.winnerA', { days: CONTRACT_PARAMS.roundDurationDays }),
    },
    {
      q: t('about.faq.payoutQ'),
      a: t('about.faq.payoutA', {
        pct: CONTRACT_PARAMS.lotteryWinnerShare,
        ownerPct: CONTRACT_PARAMS.lotteryOwnerShare,
        restPct:
          100 - CONTRACT_PARAMS.lotteryWinnerShare - CONTRACT_PARAMS.lotteryOwnerShare,
      }),
    },
    {
      q: t('about.faq.prmdQ'),
      a: t('about.faq.prmdA', {
        rate: CONTRACT_PARAMS.emissionPerSecond,
        max: formatInt(CONTRACT_PARAMS.maxSupply),
      }),
    },
    {
      q: t('about.faq.referralsQ'),
      a: t('about.faq.referralsA', {
        levels: CONTRACT_PARAMS.referralLevels,
        pct: CONTRACT_PARAMS.referralSharePercent,
      }),
    },
  ];

  return (
    <section className="content-section">
      <header className="page-head">
        <h1 className="lottery-title">{t('about.title')}</h1>
        <span className="page-head__sub">{t('about.subtitle')}</span>
      </header>

      <div className="page-grid">
        <div className="page-grid__main">
          <div className="token-info-card">
            <h3 className="card-head">
              <HelpCircle size={18} aria-hidden="true" /> {t('about.faqTitle')}
            </h3>
            {faq.map((item) => (
              <details key={item.q} className="faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="page-grid__side">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-card__label">
                <Users size={13} aria-hidden="true" /> {t('about.totalUsers')}
              </div>
              <div className="stat-card__value">
                {stat(state.isLive ? formatInt(state.system.totalUsers) : '—')}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card__label">
                <Trophy size={13} aria-hidden="true" /> {t('about.totalPaid')}
              </div>
              <div className="stat-card__value">
                {stat(`${formatTon(state.lottery.totalWinsAmount)} GRAM`)}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card__label">
                <CalendarClock size={13} aria-hidden="true" /> {t('about.roundDuration')}
              </div>
              <div className="stat-card__value">
                {t('about.roundDurationValue', { days: CONTRACT_PARAMS.roundDurationDays })}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card__label">
                <Coins size={13} aria-hidden="true" /> {t('about.minEntry')}
              </div>
              <div className="stat-card__value">{CONTRACT_PARAMS.minEntryTon} GRAM</div>
            </div>
          </div>

          <div className="token-info-card">
            <h3 className="card-head">
              <FileText size={18} aria-hidden="true" /> {t('about.contract')}
            </h3>
            <div className="detail-row">
              <span className="detail-label">
                <Network size={12} aria-hidden="true" /> {t('about.network')}
              </span>
              <span className="detail-value">{TON_NETWORK}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">
                <FileText size={12} aria-hidden="true" /> {t('about.contractAddress')}
              </span>
              <span className="detail-value detail-value--link">
                <ExplorerLink address={CONTRACT_ADDRESS || null} />
              </span>
            </div>
            <div className="verified-badges">
              <VerifiedBadge address={CONTRACT_ADDRESS || null} label={t('verified.lottery')} />
              <VerifiedBadge
                address={state.system.jettonMasterAddress}
                label={t('verified.jetton')}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
